import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Database,
    Clock,
    Loader2,
    RefreshCw,
    CheckCircle,
    XCircle,
    AlertCircle,
    FileArchive,
    Timer,
    Tag as TagIcon,
    Edit2,
    RotateCcw,
    X,
    ChevronDown,
    ChevronUp,
    History,
    Layers,
    FileText,
    Trash2
} from 'lucide-react';
import { useSnapshots, Snapshot } from '../../hooks/useSnapshots';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';
import { useFeatures } from '../../hooks/useFeatures';
import FeatureUpgradePrompt from '../common/FeatureUpgradePrompt';

interface DatabaseSectionProps {
    workspace: any;
    workspaceId: string;
    api: any;
}

// Status constants to avoid magic strings
const STATUS = {
    PENDING: 'PENDING',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETE: 'COMPLETE',
    FAILED: 'FAILED'
} as const;

const DatabaseSection: React.FC<DatabaseSectionProps> = ({ workspace, workspaceId, api }) => {
    const { isReadOnly } = useReadOnlyMode();
    const { has, loading: featuresLoading } = useFeatures();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [snapshotTags, setSnapshotTags] = useState('');
    const [snapshotDescription, setSnapshotDescription] = useState('');
    const [editingTagId, setEditingTagId] = useState<string | null>(null);
    const [editTagValue, setEditTagValue] = useState('');
    const [editDescriptionValue, setEditDescriptionValue] = useState('');
    const [restoreModalSnapshot, setRestoreModalSnapshot] = useState<Snapshot | null>(null);
    const [restoringSnapshot, setRestoringSnapshot] = useState(false);
    const [restoreError, setRestoreError] = useState<string | null>(null);
    const [highlightedRestoreId, setHighlightedRestoreId] = useState<string | null>(null);
    const [expandedSnapshotRestores, setExpandedSnapshotRestores] = useState<string | null>(null);
    const [deleteModalSnapshot, setDeleteModalSnapshot] = useState<Snapshot | null>(null);
    const [deletingSnapshot, setDeletingSnapshot] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [activeRestoreDetailsModal, setActiveRestoreDetailsModal] = useState<Snapshot | null>(null);
    const [lastRestoreDetailsModal, setLastRestoreDetailsModal] = useState<Snapshot | null>(null);
    
    const hasSnapshotsFeature = has('database-snapshots');

    const {
        snapshots,
        restores,
        loading: snapshotsLoading,
        error: snapshotsError,
        creatingSnapshot,
        cooldownStatus,
        snapshotTotal,
        hasMore,
        loadingMore,
        loadMoreSnapshots,
        fetchSnapshots,
        createSnapshot,
        updateSnapshotMetadata,
        deleteSnapshot,
        restoreSnapshot,
        fetchAllRestores,
        getSnapshotById,
        checkCooldown,
    } = useSnapshots(workspaceId, hasSnapshotsFeature);

    // Memoized sorted snapshots
    const sortedSnapshots = useMemo(() => {
        return [...snapshots].sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }, [snapshots]);

    // Get active restores only (PENDING or IN_PROGRESS)
    const activeRestores = useMemo(() => {
        return restores.filter(r => r.status === STATUS.PENDING || r.status === STATUS.IN_PROGRESS)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [restores]);

    // Get active snapshots only (PENDING or IN_PROGRESS)
    const activeSnapshots = useMemo(() => {
        return snapshots.filter(s => s.status === STATUS.PENDING || s.status === STATUS.IN_PROGRESS)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [snapshots]);

    // Get last restore (most recent non-active restore) - computed from restores array
    const lastRestore = useMemo(() => {
        if (restores.length === 0) return null;

        // Get the most recent restore (sorted by created_at descending)
        const sortedRestores = [...restores].sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        return sortedRestores[0] || null;
    }, [restores]);

    // Get snapshot for a restore operation
    const getSnapshotForRestore = useCallback((snapshotId: string) => {
        return snapshots.find(s => s.id === snapshotId);
    }, [snapshots]);

    // Get all restores for a specific snapshot from local state (used for expandable history)
    const getRestoresForSnapshot = useCallback((snapshotId: string) => {
        return restores
            .filter(r => r.snapshot_id === snapshotId)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [restores]);

    // Auto-close restore details modal when restore completes or fails
    useEffect(() => {
        if (activeRestoreDetailsModal) {
            // Check if there's still an active restore for this snapshot
            const hasActiveRestore = activeRestores.some(
                restore => restore.snapshot_id === activeRestoreDetailsModal.id
            );

            // If no active restore found, close the modal
            if (!hasActiveRestore) {
                setActiveRestoreDetailsModal(null);
            }
        }
    }, [activeRestores, activeRestoreDetailsModal]);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            // Fetch snapshots with restore summary included (efficient server-side aggregation)
            await Promise.all([fetchSnapshots(true), fetchAllRestores(), checkCooldown()]);
        } finally {
            setIsRefreshing(false);
        }
    }, [fetchSnapshots, fetchAllRestores, checkCooldown]);

    const handleCreateSnapshot = useCallback(async () => {
        try {
            setCreateError(null);

            // Validate tags
            let tagsArray = snapshotTags ? snapshotTags.split(',').map(t => t.trim()).filter(t => t) : undefined;

            // Remove duplicates (case-insensitive)
            if (tagsArray) {
                const uniqueTags = new Map<string, string>();
                tagsArray.forEach(tag => {
                    const lowerTag = tag.toLowerCase();
                    if (!uniqueTags.has(lowerTag)) {
                        uniqueTags.set(lowerTag, tag);
                    }
                });
                tagsArray = Array.from(uniqueTags.values());
            }

            if (tagsArray && tagsArray.length > 10) {
                setCreateError('Maximum 10 tags allowed');
                return;
            }
            if (tagsArray && tagsArray.some(tag => tag.length > 30)) {
                setCreateError('Each tag must be 30 characters or less');
                return;
            }

            // Validate description
            const description = snapshotDescription.trim() || undefined;
            if (description && description.length > 500) {
                setCreateError('Description must be 500 characters or less');
                return;
            }

            await createSnapshot(tagsArray, description);
            setSnapshotTags(''); // Clear tags input after successful creation
            setSnapshotDescription(''); // Clear description input after successful creation
            // Success - snapshot created
        } catch (err: any) {
            setCreateError(err.message || 'Failed to create snapshot');
        }
    }, [createSnapshot, snapshotTags, snapshotDescription]);

    const handleEditMetadata = useCallback((snapshot: Snapshot) => {
        setEditingTagId(snapshot.id);
        setEditTagValue(snapshot.tags?.join(', ') || '');
        setEditDescriptionValue(snapshot.description || '');
    }, []);

    const handleSaveMetadata = useCallback(async (snapshotId: string) => {
        try {
            // Validate tags
            let tagsArray = editTagValue ? editTagValue.split(',').map(t => t.trim()).filter(t => t) : [];

            // Remove duplicates (case-insensitive)
            if (tagsArray.length > 0) {
                const uniqueTags = new Map<string, string>();
                tagsArray.forEach(tag => {
                    const lowerTag = tag.toLowerCase();
                    if (!uniqueTags.has(lowerTag)) {
                        uniqueTags.set(lowerTag, tag);
                    }
                });
                tagsArray = Array.from(uniqueTags.values());
            }

            if (tagsArray.length > 10) {
                setCreateError('Maximum 10 tags allowed');
                return;
            }
            if (tagsArray.some(tag => tag.length > 30)) {
                setCreateError('Each tag must be 30 characters or less');
                return;
            }

            // Validate description
            const description = editDescriptionValue.trim() || null;
            if (description && description.length > 500) {
                setCreateError('Description must be 500 characters or less');
                return;
            }

            await updateSnapshotMetadata(snapshotId, tagsArray.length > 0 ? tagsArray : null, description);
            setEditingTagId(null);
            setEditTagValue('');
            setEditDescriptionValue('');
        } catch (err: any) {
            setCreateError(err.message || 'Failed to update metadata');
        }
    }, [updateSnapshotMetadata, editTagValue, editDescriptionValue]);

    const handleCancelEditMetadata = useCallback(() => {
        setEditingTagId(null);
        setEditTagValue('');
        setEditDescriptionValue('');
        setCreateError(null); // Clear any validation errors
    }, []);

    const handleToggleRestoreHistory = useCallback(async (snapshotId: string) => {
        const isExpanding = expandedSnapshotRestores !== snapshotId;
        setExpandedSnapshotRestores(prev => prev === snapshotId ? null : snapshotId);

        // Fetch restores on-demand when expanding
        if (isExpanding) {
            await fetchAllRestores();
        }
    }, [expandedSnapshotRestores, fetchAllRestores]);

    const handleOpenRestoreModal = useCallback((snapshot: Snapshot) => {
        setRestoreModalSnapshot(snapshot);
        setRestoreError(null);
    }, []);

    const handleCloseRestoreModal = useCallback(() => {
        setRestoreModalSnapshot(null);
        setRestoreError(null);
    }, []);

    const handleRestore = useCallback(async () => {
        if (!restoreModalSnapshot) return;

        setRestoringSnapshot(true);
        setRestoreError(null);

        try {
            const response = await restoreSnapshot(restoreModalSnapshot.id);

            // Highlight the new restore
            const restoreId = response.restore?.id || 'unknown';
            setHighlightedRestoreId(restoreId);

            // Expand the snapshot's restore history automatically
            setExpandedSnapshotRestores(restoreModalSnapshot.id);

            // Close modal
            handleCloseRestoreModal();

            // Refresh snapshots with restore summary
            await fetchSnapshots(true);

            // Small delay to allow state to update, then scroll
            setTimeout(() => {
                // Scroll to the snapshot (not the Recent Activity section)
                const snapshotElement = document.querySelector(`[data-snapshot-id="${restoreModalSnapshot.id}"]`);
                if (snapshotElement) {
                    snapshotElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 500);

            // Remove highlight after 15 seconds
            setTimeout(() => {
                setHighlightedRestoreId(null);
            }, 15000);
        } catch (err: any) {
            setRestoreError(err.message || 'Failed to restore snapshot');
        } finally {
            setRestoringSnapshot(false);
        }
    }, [restoreModalSnapshot, restoreSnapshot, handleCloseRestoreModal, fetchSnapshots]);

    const handleOpenDeleteModal = useCallback((snapshot: Snapshot) => {
        setDeleteModalSnapshot(snapshot);
        setDeleteError(null);
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
        setDeleteModalSnapshot(null);
        setDeleteError(null);
    }, []);

    const handleDelete = useCallback(async () => {
        if (!deleteModalSnapshot) return;

        setDeletingSnapshot(true);
        setDeleteError(null);

        try {
            await deleteSnapshot(deleteModalSnapshot.id);

            // Close modal
            handleCloseDeleteModal();

            // Refresh snapshots
            await fetchSnapshots(true);
        } catch (err: any) {
            setDeleteError(err.message || 'Failed to delete snapshot');
        } finally {
            setDeletingSnapshot(false);
        }
    }, [deleteModalSnapshot, deleteSnapshot, handleCloseDeleteModal, fetchSnapshots]);

    const getStatusIcon = useCallback((status: string, size: 'sm' | 'md' = 'md') => {
        const sizeClass = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
        switch (status) {
            case STATUS.COMPLETE:
                return <CheckCircle className={`${sizeClass} text-green-600 dark:text-green-400`} />;
            case STATUS.IN_PROGRESS:
            case STATUS.PENDING:
                return <Loader2 className={`${sizeClass} text-amber-600 dark:text-amber-400 animate-spin`} />;
            case STATUS.FAILED:
                return <XCircle className={`${sizeClass} text-red-600 dark:text-red-400`} />;
            default:
                return <AlertCircle className={`${sizeClass} text-neutral-600 dark:text-neutral-400`} />;
        }
    }, []);

    const formatDate = useCallback((dateString: string) => {
        try {
            return new Date(dateString).toLocaleString(undefined, {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
            });
        } catch {
            return 'Invalid date';
        }
    }, []);

    const formatFileSize = useCallback((bytes: number | null) => {
        if (bytes == null) return 'N/A';
        if (bytes === 0) return '0 B';
        const mb = bytes / (1024 * 1024);
        if (mb < 1) {
            const kb = bytes / 1024;
            if (kb < 1) {
                return `${bytes} B`;
            }
            return `${kb.toFixed(2)} KB`;
        }
        return `${mb.toFixed(2)} MB`;
    }, []);

    const formatNumber = useCallback((num: number | null) => {
        if (num === null || num === undefined) return null;
        return num.toLocaleString();
    }, []);

    const calculateDuration = useCallback((startDate: string | null, endDate: string | null) => {
        if (!startDate || !endDate) return null;

        const start = new Date(startDate).getTime();
        const end = new Date(endDate).getTime();
        const durationSeconds = Math.floor((end - start) / 1000);

        if (durationSeconds < 60) {
            return `${durationSeconds}s`;
        } else if (durationSeconds < 3600) {
            const minutes = Math.floor(durationSeconds / 60);
            const seconds = durationSeconds % 60;
            return `${minutes}m ${seconds}s`;
        } else {
            const hours = Math.floor(durationSeconds / 3600);
            const minutes = Math.floor((durationSeconds % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
    }, []);

    const getErrorAdvice = useCallback((errorMessage: string | null, type: 'snapshot' | 'restore') => {
        if (!errorMessage) return null;

        const lowerError = errorMessage.toLowerCase();

        // Common error patterns and advice
        if (lowerError.includes('timeout') || lowerError.includes('timed out')) {
            return 'The operation took too long to complete. This might happen with large databases. Please try again if the issue persists.';
        } else if (lowerError.includes('permission') || lowerError.includes('access denied')) {
            return 'Database access was denied. Please check your database credentials in workspace settings.';
        } else if (lowerError.includes('connection') || lowerError.includes('network')) {
            return 'Could not connect to the database. Please verify your database is running and accessible.';
        } else if (lowerError.includes('storage') || lowerError.includes('s3')) {
            return 'Storage error occurred. Please contact support if this issue persists.';
        } else if (lowerError.includes('cooldown')) {
            return 'Please wait before creating another snapshot.';
        } else if (type === 'restore' && lowerError.includes('snapshot not found')) {
            return 'The snapshot may have been deleted or is not accessible.';
        }

        return 'Please try again if the issue persists.';
    }, []);

    // Show loading state
    if (snapshotsLoading && !snapshots.length) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary dark:border-brand-primaryDarkMode"></div>
            </div>
        );
    }

    // Show upgrade prompt if feature is not available
    if (!featuresLoading && !hasSnapshotsFeature) {
        return (
            <div className="pb-4">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Database Snapshots</h2>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                            Create and manage database backups for your projects
                        </p>
                    </div>
                </div>

                <FeatureUpgradePrompt
                    icon={Database}
                    title="Upgrade to Access Database Snapshots"
                    description="Database snapshots allow you to create backups and restore your project's database to any previous state."
                />
            </div>
        );
    }

    const canCreateSnapshot = cooldownStatus?.can_create_snapshot && !isReadOnly && !creatingSnapshot && activeRestores.length === 0 && activeSnapshots.length === 0;

    return (
        <div>
            {/* Database Snapshots Section */}
            <div className="pb-4">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Database Snapshots</h2>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                            Create and manage database backups for your workspace
                        </p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleRefresh}
                            disabled={isRefreshing}
                            className="p-2 hover:bg-neutral-200 dark:hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-50"
                            title="Refresh snapshots"
                        >
                            <RefreshCw className={`w-4 h-4 text-neutral-600 dark:text-neutral-400 ${isRefreshing ? 'animate-spin' : ''
                                }`} />
                        </button>
                    </div>
                </div>

                {/* Create Snapshot Form */}
                <div className="mb-4 p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-xl border border-neutral-200 dark:border-neutral-700">
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="flex-1">
                                <div className="flex justify-between items-center mb-1">
                                    <label htmlFor="snapshot-tags" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                                        Tags (optional, comma-separated)
                                    </label>
                                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                        max 10 tags, 30 chars each
                                    </span>
                                </div>
                                <div className="relative">
                                    <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                                    <input
                                        id="snapshot-tags"
                                        type="text"
                                        value={snapshotTags}
                                        onChange={(e) => setSnapshotTags(e.target.value)}
                                        placeholder="e.g., production, backup, daily"
                                        disabled={!canCreateSnapshot}
                                        className="w-full pl-10 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50"
                                    />
                                </div>
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={handleCreateSnapshot}
                                    disabled={!canCreateSnapshot}
                                    className={`inline-flex items-center px-4 py-2 rounded-xl font-medium transition-all whitespace-nowrap ${canCreateSnapshot
                                        ? 'bg-brand-action hover:bg-brand-action-hover text-brand-action-text shadow-sm'
                                        : 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
                                        }`}
                                    title={
                                        isReadOnly ? 'Read-only mode' :
                                            activeRestores.length > 0 ? 'Cannot create snapshot while a restore is running' :
                                            activeSnapshots.length > 0 ? 'Cannot create snapshot while another snapshot is being created' :
                                            cooldownStatus?.minutes_remaining ?
                                                `Wait ${cooldownStatus.minutes_remaining} more minute(s)` :
                                                'Create snapshot'
                                    }
                                >
                                    {creatingSnapshot ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <FileArchive className="w-4 h-4 mr-2" />
                                            Snapshot
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label htmlFor="snapshot-description" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                                    Description (optional)
                                </label>
                                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                    {snapshotDescription.length}/500
                                </span>
                            </div>
                            <textarea
                                id="snapshot-description"
                                value={snapshotDescription}
                                onChange={(e) => setSnapshotDescription(e.target.value)}
                                placeholder="Add a description for this snapshot..."
                                maxLength={500}
                                rows={2}
                                disabled={!canCreateSnapshot}
                                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50 resize-none [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-track]:dark:bg-neutral-800 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Cooldown Warning */}
                {cooldownStatus && !cooldownStatus.can_create_snapshot && (
                    <div className="mb-4 p-4 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-xl flex items-start">
                        <Timer className="w-5 h-5 text-yellow-700 dark:text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                                Snapshot Cooldown Active
                            </p>
                            <p className="text-sm text-yellow-600 dark:text-yellow-500 mt-1">
                                You can create a new snapshot in {cooldownStatus.minutes_remaining} minute(s).
                            </p>
                        </div>
                    </div>
                )}

                {/* Create Error */}
                {createError && (
                    <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-xl">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">{createError}</p>
                                {getErrorAdvice(createError, 'snapshot') && (
                                    <p className="text-xs text-red-600 dark:text-red-500">
                                        {getErrorAdvice(createError, 'snapshot')}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => setCreateError(null)}
                                className="p-1 hover:bg-red-200 dark:hover:bg-red-800 rounded transition-colors"
                                title="Dismiss"
                            >
                                <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Restore Status Line - Three States */}
                {(() => {
                    // State A: Active restoration in progress
                    if (activeRestores.length > 0) {
                        return activeRestores.map((restore) => {
                            const relatedSnapshot = getSnapshotForRestore(restore.snapshot_id);
                            return (
                                <div key={restore.id} className="mb-4 flex justify-center">
                                    <button
                                        onClick={async () => {
                                            try {
                                                const snapshot = relatedSnapshot || await getSnapshotById(restore.snapshot_id);
                                                setActiveRestoreDetailsModal(snapshot);
                                            } catch (err) {
                                                console.error('Failed to fetch snapshot details:', err);
                                            }
                                        }}
                                        className="group flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-brand-primary dark:hover:text-brand-primaryDarkMode transition-colors"
                                    >
                                        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                                        <span>
                                            Restoring snapshot{' '}
                                            <code className="font-mono font-medium text-neutral-900 dark:text-white group-hover:text-brand-primary dark:group-hover:text-brand-primaryDarkMode">
                                                {restore.snapshot_id.substring(0, 8)}
                                            </code>
                                            <span className="ml-1 opacity-60">— click to view details</span>
                                        </span>
                                    </button>
                                </div>
                            );
                        });
                    }

                    // State B: No restorations at all
                    if (!lastRestore) {
                        return (
                            <div className="mb-4 flex justify-center">
                                <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-500">
                                    <Database className="w-4 h-4 flex-shrink-0" />
                                    <span>No database restorations performed yet</span>
                                </div>
                            </div>
                        );
                    }

                    // State C: Has past restorations, show last one
                    const timeAgo = (() => {
                        const now = new Date();
                        const restoreDate = new Date(lastRestore.created_at);
                        const diffMs = now.getTime() - restoreDate.getTime();

                        // Handle negative differences (future dates or clock skew)
                        if (diffMs < 0) return 'just now';

                        const diffMins = Math.floor(diffMs / 60000);
                        const diffHours = Math.floor(diffMs / 3600000);
                        const diffDays = Math.floor(diffMs / 86400000);

                        if (diffMins < 1) return 'just now';
                        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
                        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
                    })();

                    const statusIcon = lastRestore.status === 'COMPLETE'
                        ? <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                        : <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />;

                    const statusText = lastRestore.status === 'COMPLETE' ? 'completed' : 'failed';

                    return (
                        <div className="mb-4 flex justify-center">
                            <button
                                onClick={async () => {
                                    try {
                                        const snapshot = await getSnapshotById(lastRestore.snapshot_id);
                                        setLastRestoreDetailsModal(snapshot);
                                    } catch (err) {
                                        console.error('Failed to fetch snapshot details:', err);
                                    }
                                }}
                                className="group flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-brand-primary dark:hover:text-brand-primaryDarkMode transition-colors"
                            >
                                {statusIcon}
                                <span>
                                    Last restore: {statusText} {timeAgo}
                                    <span className="ml-1 opacity-60">— click to view details</span>
                                </span>
                            </button>
                        </div>
                    );
                })()}

                {/* Snapshots Error */}
                {snapshotsError ? (
                    <div className="p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-xl">
                        <p className="text-red-700 dark:text-red-400">{snapshotsError}</p>
                    </div>
                ) : sortedSnapshots.length > 0 ? (
                    <div className="space-y-4">
                        {sortedSnapshots.map((snapshot) => (
                            <div key={snapshot.id} data-snapshot-id={snapshot.id} className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
                                <div className="p-4">
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center mb-2">
                                                <Database className="w-4 h-4 text-neutral-500 dark:text-neutral-400 mr-2" />
                                                <span className="text-sm text-neutral-600 dark:text-neutral-400 mr-1.5">Snapshot</span>
                                                <code className="text-sm font-mono text-neutral-900 dark:text-neutral-100">
                                                    {snapshot.id.substring(0, 8)}
                                                </code>
                                            </div>

                                            {/* Metadata Display/Edit (Description and Tags) */}
                                            {editingTagId === snapshot.id ? (
                                                <div className="mb-2 space-y-2">
                                                    <div>
                                                        <div className="flex justify-between items-center mb-1">
                                                            <span className="text-xs text-neutral-600 dark:text-neutral-400">Description</span>
                                                            <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                                                {editDescriptionValue.length}/500
                                                            </span>
                                                        </div>
                                                        <textarea
                                                            value={editDescriptionValue}
                                                            onChange={(e) => setEditDescriptionValue(e.target.value)}
                                                            placeholder="Add description..."
                                                            maxLength={500}
                                                            rows={2}
                                                            className="w-full px-2 py-1 text-sm border border-brand-primary rounded bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-primary resize-none [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-track]:dark:bg-neutral-800 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="flex justify-between items-center mb-1">
                                                            <span className="text-xs text-neutral-600 dark:text-neutral-400">Tags (max 10, each 30 chars max)</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="relative flex-1">
                                                                <TagIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-neutral-400" />
                                                                <input
                                                                    type="text"
                                                                    value={editTagValue}
                                                                    onChange={(e) => setEditTagValue(e.target.value)}
                                                                    placeholder="Add tags (comma-separated)..."
                                                                    className="w-full pl-8 pr-2 py-1 text-sm border border-brand-primary rounded bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleSaveMetadata(snapshot.id)}
                                                            className="px-2 py-1 text-xs bg-brand-action hover:bg-brand-action-hover text-brand-action-text rounded transition-colors"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEditMetadata}
                                                            className="px-2 py-1 text-xs bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 rounded transition-colors"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {(snapshot.tags && snapshot.tags.length > 0) || snapshot.description ? (
                                                        <div className="mb-2 space-y-1">
                                                            {snapshot.description && (
                                                                <div className="flex items-start gap-2">
                                                                    <p className="text-sm text-neutral-600 dark:text-neutral-400 flex-1 break-all line-clamp-3">
                                                                        {snapshot.description}
                                                                    </p>
                                                                    {snapshot.status === STATUS.COMPLETE && !isReadOnly && !snapshot.tags?.length && (
                                                                        <button
                                                                            onClick={() => handleEditMetadata(snapshot)}
                                                                            className="p-1.5 sm:p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors flex-shrink-0"
                                                                            title="Edit metadata"
                                                                        >
                                                                            <Edit2 className="w-4 h-4 sm:w-3 sm:h-3 text-neutral-500 dark:text-neutral-400" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {snapshot.tags && snapshot.tags.length > 0 && (
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    {snapshot.tags.map((tag, index) => (
                                                                        <div key={index} className="inline-flex items-center px-2 py-1 bg-brand-primary/20 dark:bg-brand-primaryDarkMode/10 border border-brand-primary/40 dark:border-brand-primaryDarkMode/30 rounded text-xs text-neutral-700 dark:text-brand-primaryDarkMode max-w-[200px]" title={tag}>
                                                                            <TagIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                                                                            <span className="truncate">{tag}</span>
                                                                        </div>
                                                                    ))}
                                                                    {snapshot.status === STATUS.COMPLETE && !isReadOnly && (
                                                                        <button
                                                                            onClick={() => handleEditMetadata(snapshot)}
                                                                            className="p-1.5 sm:p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors"
                                                                            title="Edit metadata"
                                                                        >
                                                                            <Edit2 className="w-4 h-4 sm:w-3 sm:h-3 text-neutral-500 dark:text-neutral-400" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : snapshot.status === STATUS.COMPLETE && !isReadOnly ? (
                                                        <button
                                                            onClick={() => handleEditMetadata(snapshot)}
                                                            className="inline-flex items-center gap-1.5 mb-2 px-2 py-1.5 sm:px-0 sm:py-0 text-sm sm:text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 sm:hover:bg-transparent rounded sm:rounded-none transition-colors"
                                                        >
                                                            <Edit2 className="w-4 h-4 sm:w-3 sm:h-3" />
                                                            Add description & tags
                                                        </button>
                                                    ) : null}
                                                </>
                                            )}

                                            <div className="grid grid-cols-2 sm:flex sm:items-center sm:flex-wrap gap-2 sm:gap-x-3 sm:gap-y-1 text-sm text-neutral-600 dark:text-neutral-400">
                                                <span className="col-span-2 sm:col-span-1 text-sm sm:text-xs font-medium">Snapshot:</span>
                                                <div className="flex items-center gap-1 cursor-help" title="Snapshot status - indicates whether the snapshot creation succeeded or is in progress">
                                                    {getStatusIcon(snapshot.status, 'sm')}
                                                    <span className="text-sm sm:text-xs font-medium">{snapshot.status.replace(/_/g, ' ')}</span>
                                                </div>
                                                <div className="flex items-center gap-1 cursor-help" title="When this snapshot was created">
                                                    <Clock className="w-3 h-3 sm:w-3 sm:h-3" />
                                                    <span className="text-sm sm:text-xs">{formatDate(snapshot.created_at)}</span>
                                                </div>
                                                {snapshot.source_uncompressed_total_size_bytes != null && (
                                                    <div className="flex items-center gap-1 cursor-help" title="Uncompressed database size - the actual size of your database at the time of snapshot">
                                                        <Database className="w-3 h-3" />
                                                        <span className="text-sm sm:text-xs">{formatFileSize(snapshot.source_uncompressed_total_size_bytes)}</span>
                                                    </div>
                                                )}
                                                {snapshot.source_collection_count !== null && (
                                                    <div className="flex items-center gap-1 cursor-help" title="Number of collections in the database at the time of snapshot">
                                                        <Layers className="w-3 h-3" />
                                                        <span className="text-sm sm:text-xs">{formatNumber(snapshot.source_collection_count)} collections</span>
                                                    </div>
                                                )}
                                                {snapshot.source_document_count !== null && (
                                                    <div className="flex items-center gap-1 cursor-help" title="Total number of documents across all collections at the time of snapshot">
                                                        <FileText className="w-3 h-3" />
                                                        <span className="text-sm sm:text-xs">{formatNumber(snapshot.source_document_count)} docs</span>
                                                    </div>
                                                )}
                                            </div>
                                            {snapshot.error && (
                                                <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 italic">
                                                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                                    <span>An error occurred - please try again</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col sm:items-end gap-3 w-full sm:w-auto">
                                            {snapshot.status === STATUS.COMPLETE && !isReadOnly && (
                                                <>
                                                    <button
                                                        onClick={() => handleOpenRestoreModal(snapshot)}
                                                        disabled={activeRestores.length > 0 || activeSnapshots.length > 0}
                                                        className="inline-flex items-center justify-center px-4 py-2 sm:px-3 sm:py-1.5 text-sm sm:text-xs font-medium bg-brand-action hover:bg-brand-action-hover text-brand-action-text rounded-lg transition-colors w-full sm:w-[90px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-action"
                                                        title={activeRestores.length > 0 ? "Cannot restore while another restore is running" : activeSnapshots.length > 0 ? "Cannot restore while a snapshot is being created" : "Restore this snapshot"}
                                                    >
                                                        <RotateCcw className="w-4 h-4 sm:w-3 sm:h-3 mr-1.5 sm:mr-1" />
                                                        Restore
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenDeleteModal(snapshot)}
                                                        disabled={activeRestores.length > 0 || activeSnapshots.length > 0}
                                                        className="inline-flex items-center justify-center px-4 py-2 sm:px-3 sm:py-1.5 text-sm sm:text-xs font-medium bg-red-600 hover:bg-red-700 text-black rounded-lg transition-colors w-full sm:w-[90px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
                                                        title={activeRestores.length > 0 ? "Cannot delete while a restore is running" : activeSnapshots.length > 0 ? "Cannot delete while a snapshot is being created" : "Delete this snapshot"}
                                                    >
                                                        <Trash2 className="w-4 h-4 sm:w-3 sm:h-3 mr-1.5 sm:mr-1" />
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                            {snapshot.status === STATUS.FAILED && !isReadOnly && (
                                                <button
                                                    onClick={() => handleOpenDeleteModal(snapshot)}
                                                    disabled={activeRestores.length > 0 || activeSnapshots.length > 0}
                                                    className="inline-flex items-center justify-center px-4 py-2 sm:px-3 sm:py-1.5 text-sm sm:text-xs font-medium bg-red-600 hover:bg-red-700 text-black rounded-lg transition-colors w-full sm:w-[90px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
                                                    title={activeRestores.length > 0 ? "Cannot delete while a restore is running" : activeSnapshots.length > 0 ? "Cannot delete while a snapshot is being created" : "Delete this failed snapshot"}
                                                >
                                                    <Trash2 className="w-4 h-4 sm:w-3 sm:h-3 mr-1.5 sm:mr-1" />
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Restore History Info for COMPLETE snapshots */}
                                    {snapshot.status === STATUS.COMPLETE && (() => {
                                        // Use server-provided restore_summary for efficient display
                                        const summary = snapshot.restore_summary;
                                        const totalCount = summary?.total_count || 0;
                                        const lastRestore = summary?.last_restore || null;

                                        return (
                                            <div className="mt-3 pt-3 -mx-4 px-4 border-t-2 border-neutral-300 dark:border-neutral-600">
                                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <History className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                                                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                                                            Restore History
                                                        </span>
                                                    </div>
                                                    {totalCount > 0 && (
                                                        <button
                                                            onClick={() => handleToggleRestoreHistory(snapshot.id)}
                                                            className="inline-flex items-center gap-1 px-3 py-2 sm:px-2 sm:py-1 text-sm sm:text-xs text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors self-start sm:self-auto"
                                                        >
                                                            {expandedSnapshotRestores === snapshot.id ? (
                                                                <>
                                                                    <ChevronUp className="w-4 h-4 sm:w-3 sm:h-3" />
                                                                    Hide
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <ChevronDown className="w-4 h-4 sm:w-3 sm:h-3" />
                                                                    View full history
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Last Restore Summary */}
                                                {lastRestore && (
                                                    <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1 text-sm sm:text-xs text-neutral-600 dark:text-neutral-400">
                                                        <span className="font-medium">Last restoration:</span>
                                                        <div className="flex items-center gap-1">
                                                            {getStatusIcon(lastRestore.status, 'sm')}
                                                            <span className="font-medium">{lastRestore.status.replace(/_/g, ' ')}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            <span>{formatDate(lastRestore.created_at)}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                {totalCount === 0 && (
                                                    <p className="mt-2 text-sm sm:text-xs text-neutral-500 dark:text-neutral-500 italic">
                                                        This snapshot has never been restored
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Expandable Restore History - Compact View */}
                                    {snapshot.status === STATUS.COMPLETE && expandedSnapshotRestores === snapshot.id && (() => {
                                        const snapshotRestores = getRestoresForSnapshot(snapshot.id);
                                        return snapshotRestores.length > 0 && (
                                            <div className="mt-3 pt-3 -mx-4 px-4 border-t border-neutral-200 dark:border-neutral-700">
                                                <div className="space-y-1.5">
                                                    {snapshotRestores.map((restore) => {
                                                        const isHighlighted = restore.id === highlightedRestoreId;
                                                        const duration = calculateDuration(restore.started_at, restore.completed_at);

                                                        return (
                                                            <div
                                                                key={restore.id}
                                                                className={`flex items-center flex-wrap gap-x-3 gap-y-1.5 px-3 py-2.5 sm:px-3 sm:py-2 rounded-lg transition-all duration-300 text-sm sm:text-xs ${
                                                                    isHighlighted
                                                                        ? 'bg-green-50 dark:bg-green-900/10 border border-green-300 dark:border-green-700'
                                                                        : 'bg-neutral-50 dark:bg-neutral-900 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                                                                }`}
                                                            >
                                                                {/* Status */}
                                                                <div className="flex items-center gap-1">
                                                                    {getStatusIcon(restore.status, 'sm')}
                                                                    <span className="font-medium text-neutral-600 dark:text-neutral-400">{restore.status.replace(/_/g, ' ')}</span>
                                                                </div>

                                                                {isHighlighted && (
                                                                    <span className="px-1.5 py-0.5 text-xs font-bold bg-green-500 text-white rounded animate-pulse">
                                                                        NEW
                                                                    </span>
                                                                )}

                                                                {/* Time */}
                                                                <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                                    <Clock className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
                                                                    <span>{formatDate(restore.created_at)}</span>
                                                                </div>

                                                                {/* Duration - Only show for completed restores */}
                                                                {duration && (
                                                                    <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                                        <Timer className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
                                                                        <span>{duration}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : snapshotsLoading ? (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary dark:border-brand-primaryDarkMode"></div>
                    </div>
                ) : (
                    <div className="text-center py-8 bg-neutral-50 dark:bg-neutral-800/50 rounded-xl border border-neutral-200 dark:border-neutral-700">
                        <Database className="w-12 h-12 text-neutral-400 dark:text-neutral-600 mx-auto mb-3" />
                        <p className="text-neutral-600 dark:text-neutral-400">No snapshots found for this workspace.</p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-500 mt-1">
                            Create your first snapshot to back up your database.
                        </p>
                    </div>
                )}

                {/* Load More Button */}
                {!snapshotsLoading && sortedSnapshots.length > 0 && hasMore && (
                    <div className="mt-4 flex items-center justify-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
                        <span>
                            {sortedSnapshots.length} of {snapshotTotal}
                        </span>
                        <div className="w-px h-3 bg-neutral-300 dark:bg-neutral-600"></div>
                        <button
                            onClick={() => loadMoreSnapshots(true)}
                            disabled={loadingMore}
                            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-brand-primary dark:text-brand-primaryDarkMode hover:bg-brand-primary/10 dark:hover:bg-brand-primaryDarkMode/10 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                            {loadingMore ? (
                                <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    <span>Loading</span>
                                </>
                            ) : (
                                <>
                                    <ChevronDown className="w-3 h-3" />
                                    <span>Load More</span>
                                </>
                            )}
                        </button>
                    </div>
                )}

                {/* All Snapshots Loaded Message */}
                {!snapshotsLoading && sortedSnapshots.length > 0 && !hasMore && snapshotTotal > 3 && (
                    <div className="mt-4 flex justify-center">
                        <div className="text-xs text-neutral-400 dark:text-neutral-500">
                            All {snapshotTotal} snapshots loaded
                        </div>
                    </div>
                )}


                {/* Restore Confirmation Modal */}
                {restoreModalSnapshot && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                        Restore Snapshot
                                    </h3>
                                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                                        This will restore the snapshot to your workspace database
                                    </p>
                                </div>
                                <button
                                    onClick={handleCloseRestoreModal}
                                    disabled={restoringSnapshot}
                                    className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={restoringSnapshot ? "Cannot close while restoring" : "Close"}
                                >
                                    <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                                </button>
                            </div>

                            <div className="mb-4 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                <div className="text-sm space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-neutral-600 dark:text-neutral-400">Snapshot ID:</span>
                                        <code className="text-neutral-900 dark:text-neutral-100 font-mono">
                                            {restoreModalSnapshot.id.substring(0, 8)}
                                        </code>
                                    </div>
                                    {restoreModalSnapshot.tags && restoreModalSnapshot.tags.length > 0 && (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-neutral-600 dark:text-neutral-400">Tags:</span>
                                            <div className="flex flex-wrap gap-1">
                                                {restoreModalSnapshot.tags.map((tag, index) => (
                                                    <span key={index} className="inline-flex items-center px-2 py-0.5 bg-brand-primary/20 dark:bg-brand-primaryDarkMode/10 border border-brand-primary/40 dark:border-brand-primaryDarkMode/30 rounded text-xs text-neutral-700 dark:text-brand-primaryDarkMode truncate max-w-[150px]" title={tag}>
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {restoreModalSnapshot.description && (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-neutral-600 dark:text-neutral-400">Description:</span>
                                            <p className="text-neutral-900 dark:text-neutral-100 text-sm max-h-32 overflow-y-auto break-all [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-track]:dark:bg-neutral-800 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                                                {restoreModalSnapshot.description}
                                            </p>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                            <Clock className="w-3 h-3" />
                                            <span>Created:</span>
                                        </div>
                                        <span className="text-neutral-900 dark:text-neutral-100">
                                            {formatDate(restoreModalSnapshot.created_at)}
                                        </span>
                                    </div>
                                    {restoreModalSnapshot.source_uncompressed_total_size_bytes != null && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <Database className="w-3 h-3" />
                                                <span>Database:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100">
                                                {formatFileSize(restoreModalSnapshot.source_uncompressed_total_size_bytes)}
                                            </span>
                                        </div>
                                    )}
                                    {restoreModalSnapshot.source_collection_count !== null && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <Layers className="w-3 h-3" />
                                                <span>Collections:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100">
                                                {formatNumber(restoreModalSnapshot.source_collection_count)}
                                            </span>
                                        </div>
                                    )}
                                    {restoreModalSnapshot.source_document_count !== null && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <FileText className="w-3 h-3" />
                                                <span>Documents:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100">
                                                {formatNumber(restoreModalSnapshot.source_document_count)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-yellow-800 dark:text-yellow-300">
                                        <p className="font-medium mb-1">Warning</p>
                                        <p>This will restore the snapshot to your workspace's current database. This operation cannot be undone.</p>
                                    </div>
                                </div>
                            </div>

                            {restoreError && (
                                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
                                    <p className="text-sm text-red-700 dark:text-red-400">{restoreError}</p>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={handleCloseRestoreModal}
                                    disabled={restoringSnapshot}
                                    className="flex-1 px-4 py-2 text-sm font-medium bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRestore}
                                    disabled={restoringSnapshot}
                                    className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium bg-brand-action hover:bg-brand-action-hover text-brand-action-text rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {restoringSnapshot ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Initiating restore...
                                        </>
                                    ) : (
                                        <>
                                            <RotateCcw className="w-4 h-4 mr-2" />
                                            Restore Database
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Snapshot Confirmation Modal */}
                {deleteModalSnapshot && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                        Delete Snapshot
                                    </h3>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                        Snapshot ID: {deleteModalSnapshot.id.substring(0, 8)}
                                    </p>
                                </div>
                                <button
                                    onClick={handleCloseDeleteModal}
                                    disabled={deletingSnapshot}
                                    className="p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors"
                                >
                                    <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                                </button>
                            </div>

                            <div className="mb-6 space-y-4">
                                <div className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-900 rounded-lg p-4 max-h-64 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-track]:dark:bg-neutral-800 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                                    {deleteModalSnapshot.tags && deleteModalSnapshot.tags.length > 0 && (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-neutral-600 dark:text-neutral-400">Tags:</span>
                                            <div className="flex flex-wrap gap-1">
                                                {deleteModalSnapshot.tags.map((tag, index) => (
                                                    <span key={index} className="inline-flex items-center px-2 py-0.5 bg-brand-primary/20 dark:bg-brand-primaryDarkMode/10 border border-brand-primary/40 dark:border-brand-primaryDarkMode/30 rounded text-xs text-neutral-700 dark:text-brand-primaryDarkMode truncate max-w-[150px]" title={tag}>
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {deleteModalSnapshot.description && (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-neutral-600 dark:text-neutral-400">Description:</span>
                                            <p className="text-neutral-900 dark:text-neutral-100 text-sm max-h-32 overflow-y-auto break-all [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-track]:dark:bg-neutral-800 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                                                {deleteModalSnapshot.description}
                                            </p>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                            <Clock className="w-3 h-3" />
                                            <span>Created:</span>
                                        </div>
                                        <span className="text-neutral-900 dark:text-neutral-100 font-medium">
                                            {formatDate(deleteModalSnapshot.created_at)}
                                        </span>
                                    </div>
                                    {deleteModalSnapshot.restore_summary && deleteModalSnapshot.restore_summary.total_count > 0 && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <History className="w-3 h-3" />
                                                <span>Restores:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100 font-medium">
                                                {deleteModalSnapshot.restore_summary.total_count} record{deleteModalSnapshot.restore_summary.total_count !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-500 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-red-800 dark:text-red-300">
                                        <p className="font-medium mb-1">Warning: Permanent Deletion</p>
                                        <p>This will permanently delete the snapshot and all associated restore records. This operation cannot be undone.</p>
                                    </div>
                                </div>
                            </div>

                            {deleteError && (
                                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
                                    <p className="text-sm text-red-700 dark:text-red-400">{deleteError}</p>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={handleCloseDeleteModal}
                                    disabled={deletingSnapshot}
                                    className="flex-1 px-4 py-2 text-sm font-medium bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deletingSnapshot}
                                    className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-black rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {deletingSnapshot ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete Snapshot
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Active Restore Details Modal */}
                {activeRestoreDetailsModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                        Snapshot Being Restored
                                    </h3>
                                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                                        This snapshot is currently being restored to your workspace
                                    </p>
                                </div>
                                <button
                                    onClick={() => setActiveRestoreDetailsModal(null)}
                                    className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors"
                                    title="Close"
                                >
                                    <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                                </button>
                            </div>

                            <div className="mb-4 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                <div className="text-sm space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-neutral-600 dark:text-neutral-400">Snapshot ID:</span>
                                        <code className="text-neutral-900 dark:text-neutral-100 font-mono">
                                            {activeRestoreDetailsModal.id.substring(0, 8)}
                                        </code>
                                    </div>
                                    {activeRestoreDetailsModal.tags && activeRestoreDetailsModal.tags.length > 0 && (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-neutral-600 dark:text-neutral-400">Tags:</span>
                                            <div className="flex flex-wrap gap-1">
                                                {activeRestoreDetailsModal.tags.map((tag, index) => (
                                                    <span key={index} className="inline-flex items-center px-2 py-0.5 bg-brand-primary/20 dark:bg-brand-primaryDarkMode/10 border border-brand-primary/40 dark:border-brand-primaryDarkMode/30 rounded text-xs text-neutral-700 dark:text-brand-primaryDarkMode truncate max-w-[150px]" title={tag}>
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {activeRestoreDetailsModal.description && (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-neutral-600 dark:text-neutral-400">Description:</span>
                                            <p className="text-neutral-900 dark:text-neutral-100 text-sm max-h-32 overflow-y-auto break-all [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-track]:dark:bg-neutral-800 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                                                {activeRestoreDetailsModal.description}
                                            </p>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                            <Clock className="w-3 h-3" />
                                            <span>Created:</span>
                                        </div>
                                        <span className="text-neutral-900 dark:text-neutral-100">
                                            {formatDate(activeRestoreDetailsModal.created_at)}
                                        </span>
                                    </div>
                                    {activeRestoreDetailsModal.source_uncompressed_total_size_bytes != null && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <Database className="w-3 h-3" />
                                                <span>Database:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100">
                                                {formatFileSize(activeRestoreDetailsModal.source_uncompressed_total_size_bytes)}
                                            </span>
                                        </div>
                                    )}
                                    {activeRestoreDetailsModal.source_collection_count !== null && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <Layers className="w-3 h-3" />
                                                <span>Collections:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100">
                                                {formatNumber(activeRestoreDetailsModal.source_collection_count)}
                                            </span>
                                        </div>
                                    )}
                                    {activeRestoreDetailsModal.source_document_count !== null && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <FileText className="w-3 h-3" />
                                                <span>Documents:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100">
                                                {formatNumber(activeRestoreDetailsModal.source_document_count)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 p-3 bg-brand-primary/5 dark:bg-brand-primaryDarkMode/5 border border-brand-primary/30 dark:border-brand-primaryDarkMode/30 rounded-lg">
                                <Loader2 className="w-5 h-5 text-brand-primary dark:text-brand-primaryDarkMode animate-spin flex-shrink-0" />
                                <p className="text-sm text-neutral-700 dark:text-neutral-300">
                                    Restoration in progress. This may take several minutes depending on database size.
                                </p>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={() => setActiveRestoreDetailsModal(null)}
                                    className="px-4 py-2 text-sm font-medium bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Last Restore Details Modal */}
                {lastRestoreDetailsModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                        Last Restored Snapshot
                                    </h3>
                                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                                        Details of the snapshot that was restored
                                    </p>
                                </div>
                                <button
                                    onClick={() => setLastRestoreDetailsModal(null)}
                                    className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors"
                                    title="Close"
                                >
                                    <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                                </button>
                            </div>

                            <div className="mb-4 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                <div className="text-sm space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-neutral-600 dark:text-neutral-400">Snapshot ID:</span>
                                        <code className="text-neutral-900 dark:text-neutral-100 font-mono">
                                            {lastRestoreDetailsModal.id.substring(0, 8)}
                                        </code>
                                    </div>
                                    {lastRestoreDetailsModal.tags && lastRestoreDetailsModal.tags.length > 0 && (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-neutral-600 dark:text-neutral-400">Tags:</span>
                                            <div className="flex flex-wrap gap-1">
                                                {lastRestoreDetailsModal.tags.map((tag, index) => (
                                                    <span key={index} className="inline-flex items-center px-2 py-0.5 bg-brand-primary/20 dark:bg-brand-primaryDarkMode/10 border border-brand-primary/40 dark:border-brand-primaryDarkMode/30 rounded text-xs text-neutral-700 dark:text-brand-primaryDarkMode truncate max-w-[150px]" title={tag}>
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {lastRestoreDetailsModal.description && (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-neutral-600 dark:text-neutral-400">Description:</span>
                                            <p className="text-neutral-900 dark:text-neutral-100 text-sm max-h-32 overflow-y-auto break-all [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-track]:dark:bg-neutral-800 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                                                {lastRestoreDetailsModal.description}
                                            </p>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                            <Clock className="w-3 h-3" />
                                            <span>Created:</span>
                                        </div>
                                        <span className="text-neutral-900 dark:text-neutral-100">
                                            {formatDate(lastRestoreDetailsModal.created_at)}
                                        </span>
                                    </div>
                                    {lastRestoreDetailsModal.source_uncompressed_total_size_bytes != null && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <Database className="w-3 h-3" />
                                                <span>Database:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100">
                                                {formatFileSize(lastRestoreDetailsModal.source_uncompressed_total_size_bytes)}
                                            </span>
                                        </div>
                                    )}
                                    {lastRestoreDetailsModal.source_collection_count !== null && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <Layers className="w-3 h-3" />
                                                <span>Collections:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100">
                                                {formatNumber(lastRestoreDetailsModal.source_collection_count)}
                                            </span>
                                        </div>
                                    )}
                                    {lastRestoreDetailsModal.source_document_count !== null && (
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                                                <FileText className="w-3 h-3" />
                                                <span>Documents:</span>
                                            </div>
                                            <span className="text-neutral-900 dark:text-neutral-100">
                                                {formatNumber(lastRestoreDetailsModal.source_document_count)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={() => setLastRestoreDetailsModal(null)}
                                    className="px-4 py-2 text-sm font-medium bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DatabaseSection;
